import { useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PolicyRule {
  key: string;
  test: (pw: string) => boolean;
}

const POLICY_RULES: PolicyRule[] = [
  { key: 'minLength', test: (pw) => pw.length >= 12 },
  { key: 'uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'number', test: (pw) => /\d/.test(pw) },
];

export function ResetPassword() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpDigits, setTotpDigits] = useState(['', '', '', '', '', '']);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTotpField, setShowTotpField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const totpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setTotpRef = useCallback((index: number) => (el: HTMLInputElement | null) => {
    totpRefs.current[index] = el;
  }, []);

  function handleTotpDigitChange(index: number, value: string) {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 1) {
      // Multi-character = paste fallback
      const next = [...totpDigits];
      cleaned.slice(0, 6).split('').forEach((char, i) => {
        if (i < 6) next[i] = char;
      });
      setTotpDigits(next);
      totpRefs.current[Math.min(cleaned.length, 5)]?.focus();
      return;
    }
    if (!/^\d?$/.test(value)) return;
    const next = [...totpDigits];
    next[index] = value;
    setTotpDigits(next);
    if (value && index < 5) {
      totpRefs.current[index + 1]?.focus();
    }
  }

  function handleTotpKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !totpDigits[index] && index > 0) {
      totpRefs.current[index - 1]?.focus();
    }
  }

  function handleTotpPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...totpDigits];
    paste.split('').forEach((char, i) => {
      if (i < 6) next[i] = char;
    });
    setTotpDigits(next);
    const nextIndex = Math.min(paste.length, 5);
    totpRefs.current[nextIndex]?.focus();
  }

  const policyResults = useMemo(
    () => POLICY_RULES.map((rule) => ({ ...rule, label: t(`passwordRules.${rule.key}`), passed: rule.test(newPassword) })),
    [newPassword, t],
  );

  const allPolicyPassed = policyResults.every((r) => r.passed);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allPolicyPassed && passwordsMatch && !isLoading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setIsLoading(true);

    try {
      const body: Record<string, string> = { token: token!, newPassword };
      const totpCode = totpDigits.join('');
      if (showTotpField && totpCode.length === 6) {
        body.totpCode = totpCode;
      }

      const res = await fetch('/internal/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Backend sends { error: 'TOTP_REQUIRED', message: '...' } — error is a string, not an object
        const errorCode = data?.error?.code || data?.code || (typeof data?.error === 'string' ? data.error : '');
        const errorMessage = data?.error?.message || data?.message || 'Password reset failed.';

        if (errorCode === 'TOTP_REQUIRED') {
          setShowTotpField(true);
          setError(t('resetPassword.totpRequired'));
          return;
        }

        setError(errorMessage);
        return;
      }

      setSuccess(true);
    } catch {
      setError(t('resetPassword.networkError'));
    } finally {
      setIsLoading(false);
    }
  }

  // ── No token: show error ────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
        <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
          <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
        </div>

        <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full flex items-center justify-center">
          <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-red-500/20 border border-red-400/30 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t('resetPassword.invalidLink')}</h2>
                <p className="text-white/60 mt-2 text-sm leading-relaxed">
                  {t('resetPassword.invalidLinkDesc')}
                </p>
              </div>
            </div>

            <Link
              to="/login"
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 flex items-center justify-center gap-2"
            >
              {t('resetPassword.goToSignIn')}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 text-center z-10">
          <p className="text-white/25 text-xs">&copy; 2026 Unifiber Belgium</p>
        </div>
      </div>
    );
  }

  // ── Success state ───────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
        <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
          <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
        </div>

        <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full flex items-center justify-center">
          <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t('resetPassword.successTitle')}</h2>
                <p className="text-white/60 mt-2 text-sm leading-relaxed">
                  {t('resetPassword.successDesc')}
                </p>
              </div>
            </div>

            <Link
              to="/login"
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 flex items-center justify-center gap-2"
            >
              {t('resetPassword.goToSignIn')}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 text-center z-10">
          <p className="text-white/25 text-xs">&copy; 2026 Unifiber Belgium</p>
        </div>
      </div>
    );
  }

  // ── Reset form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      {/* Glass card */}
      <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full flex items-center justify-center">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6">
          {/* Header */}
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <KeyRound className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('resetPassword.title')}</h2>
              <p className="text-white/60 mt-1 text-sm">
                {t('resetPassword.subtitle')}
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                {error}
                {/* Show "Request new link" for expired/invalid token errors */}
                {(error.toLowerCase().includes('expired') || error.toLowerCase().includes('invalid token')) && (
                  <Link
                    to="/forgot-password"
                    className="block mt-2 text-brand-rose hover:text-brand-orange transition-colors underline underline-offset-2"
                  >
                    {t('resetPassword.requestNewLink')}
                  </Link>
                )}
              </div>
            )}

            {/* New Password */}
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-white/80 mb-1.5">
                {t('resetPassword.newPasswordLabel')}
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  disabled={isLoading}
                  autoComplete="new-password"
                  className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password policy checklist */}
            {newPassword.length > 0 && (
              <ul className="space-y-1.5 pl-1">
                {policyResults.map((rule) => (
                  <li key={rule.label} className="flex items-center gap-2 text-xs">
                    {rule.passed ? (
                      <CheckCircle className="w-3.5 h-3.5 text-white shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    )}
                    <span className={rule.passed ? 'text-white font-medium' : 'text-white/50'}>
                      {rule.label}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-white/80 mb-1.5">
                {t('resetPassword.confirmLabel')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('resetPassword.confirmPlaceholder')}
                  disabled={isLoading}
                  autoComplete="new-password"
                  className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                  {t('resetPassword.passwordsDoNotMatch', { defaultValue: 'Passwords do not match' })}
                </p>
              )}
              {passwordsMatch && (
                <p className="mt-1.5 text-xs text-white font-medium flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                  {t('resetPassword.passwordsMatch', { defaultValue: 'Passwords match' })}
                </p>
              )}
            </div>

            {/* TOTP Code (shown only when server requests it) */}
            {showTotpField && (
              <div>
                <p className="text-sm font-medium text-white/80 mb-3 text-center">
                  {t('resetPassword.totpPrompt')}
                </p>
                <div className="flex justify-center gap-2 sm:gap-3">
                  {totpDigits.map((digit, i) => (
                    <span key={i} className="contents">
                      {i === 3 && (
                        <div className="flex items-center text-white/30 text-xl font-light">&ndash;</div>
                      )}
                      <input
                        ref={setTotpRef(i)}
                        type="text"
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleTotpDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleTotpKeyDown(i, e)}
                        onPaste={handleTotpPaste}
                        placeholder="·"
                        disabled={isLoading}
                        autoFocus={i === 0}
                        className="glass-input w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold rounded-lg text-white placeholder:text-white/25 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 caret-white disabled:opacity-50"
                      />
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {isLoading ? t('resetPassword.submitting') : t('resetPassword.submit')}
            </button>
          </form>

          {/* Back to login */}
          <div className="pt-2 text-center">
            <Link
              to="/login"
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              {t('resetPassword.backToSignIn')}
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">&copy; 2026 Unifiber Belgium</p>
      </div>
    </div>
  );
}
